export const onboardingSteps = [
  {
    id: 'welcome',
    title: 'Welcome to Blueprint',
    content: "Welcome to Blueprint! I'm here to help you set up your AI assistant workspace. Blueprint enables you to have an always-on AI teammate that can handle emails, manage projects, schedule meetings, and so much more. Over the next few minutes, I'll guide you through setting up Alex, your first AI agent. You can exit this tutorial at any time by clicking the X button, but trust me - you'll want to see this through. Let's get started!",
    targetRoute: null, // Stay on current page
    requiresAction: false, // Just informational
    autoAdvance: false, // User must click Next
    completionCheck: null,
  },
  {
    id: 'create-agent',
    title: 'Create Your Agent Alex',
    content: "First, let's create Alex - your AI project assistant. Alex will be your always-on teammate, helping you manage tasks, communicate with your team, and stay organized. You'll see a simple form asking for three things: an email address for Alex to use (I recommend creating a dedicated email like alex@yourcompany.com), a timezone, and a language. Fill in these details and click 'Create Agent'. Once created, we'll move on to setting up your subscription.",
    targetRoute: '/admin/agents/new',
    requiresAction: true,
    autoAdvance: true, // Auto-advance when agent is created
    completionCheck: 'agent-created',
  },
  {
    id: 'select-subscription',
    title: 'Select Your Subscription',
    content: "Great! Now let's activate Alex with a subscription. You'll be taken to the subscriptions page where you can choose a plan that fits your needs. We offer flexible monthly and annual billing options. Once you complete the checkout process with Stripe, we'll automatically move you to the next step where you can connect your tools and integrations.",
    targetRoute: '/admin/subscriptions',
    requiresAction: true,
    autoAdvance: true, // Auto-advance when subscription is active
    completionCheck: 'subscription-active',
  },
  {
    id: 'connect-integrations',
    title: 'Connect Your Tools',
    content: "Perfect! Now let's connect Alex to your tools. The Integrations tab will open automatically. There you'll be able to connect:\n\n• Google Workspace - so Alex can manage your emails\n• Project Management tools - like Azure, Asana, Jira, GitHub, or Linear\n\nNote: Slack will be enabled once your agent is ready and activated.\n\nClick on each integration card, authenticate, and make sure to invite Alex to your organization. You can connect as many or as few as you like right now - you can always add more later! When you're ready, click 'Next' to continue.",
    targetRoute: '/admin/agents/:agentId?tab=integrations',
    requiresAction: true,
    autoAdvance: false, // User-driven, clicks Next when ready
    completionCheck: null,
  },
  {
    id: 'build-people-directory',
    title: 'Build People Directory',
    content: "Almost done! Let's add your team members to the People Directory. This helps Alex understand who's who - their roles, responsibilities, and how to communicate with them. Click 'Add Person' and fill in basic information: name, role, email, and a brief description of what they do. Start with your key team members. You can always add more later. This context is crucial for Alex to route information correctly and understand team dynamics. Click 'Next' when you're ready to continue.",
    targetRoute: '/admin/people-directory',
    requiresAction: true,
    autoAdvance: false, // User-driven, clicks Next when ready
    completionCheck: null,
  },
  {
    id: 'add-documentation',
    title: 'Add Documentation',
    content: "Now let's teach Alex about your team's processes. The Knowledge Base will open automatically. There you can upload documents, add URLs, or create text entries about how your team works. Think standard operating procedures, project workflows, coding standards, meeting protocols - anything that helps Alex understand how you operate. Click 'Add Files', 'Add URL', or 'Create Text' to add your first document. The more context Alex has, the better decisions it can make. Click 'Next' when you're ready to continue!",
    targetRoute: '/admin/knowledge-base',
    requiresAction: true,
    autoAdvance: false, // User-driven, clicks Next when ready
    completionCheck: null,
  },
  {
    id: 'agent-activation',
    title: 'Agent Setup in Progress',
    content: "Your agent is being prepared! Our team is configuring Alex's remote desktop environment. This typically takes less than 4 hours.\n\nYou'll receive an email when Alex is ready. Feel free to close this page - your progress is saved and you can continue from here when you return.\n\nIn the meantime, you can explore the platform or come back later when you receive the notification email.",
    targetRoute: '/admin/agents/:agentId',
    requiresAction: true,
    autoAdvance: false,
    completionCheck: null,
    waitForAgentActive: true, // Disable Next until agent is active
  },
  {
    id: 'connect-slack',
    title: 'Connect with Alex on Slack',
    content: "Alex is ready! Now let's get you connected on Slack.\n\n**Why Slack?**\n\nSlack is Alex's primary communication channel. This is where you'll interact with Alex daily.\n\n**Setup Instructions:**\n\n1. Click on the Slack integration card below (it's now available with your paid subscription)\n2. Authenticate and connect your Slack workspace\n3. Invite your agent's email to your Slack workspace\n4. **Important:** Add Alex to your project channels! Alex can only help in channels where invited\n\n**What Alex Can Do on Slack:**\n\n• Respond to direct messages\n• Participate in channels where invited\n• Provide updates and notifications\n• Answer questions about your projects\n• Help coordinate with your team\n\n**Get Started:**\n\nSend Alex a direct message to introduce yourself! Try: \"Hi Alex, I'm [your name]. Can you help me with my projects?\"\n\nThen invite Alex to your main project channels so Alex can stay in the loop and help with your work.\n\nClick 'Next' when you're ready to learn about meetings.",
    targetRoute: '/admin/agents/:agentId?tab=integrations',
    requiresAction: false,
    autoAdvance: false,
    completionCheck: null,
  },
  {
    id: 'invite-to-meetings',
    title: 'Invite Alex to Meetings',
    content: "Let's get Alex into your meetings!\n\n**Request via Slack**\n\nYou can ask Alex to join a Google Meet anytime by sending a message in Slack:\n\"Alex, join my meeting at [meeting link]\"\n\nAlex works with Google Meet.\n\n**What Alex Does in Meetings:**\n• Takes notes automatically\n• Tracks action items\n• Answers questions\n• Provides context from your knowledge base\n\nYou're all set! Click 'Complete Setup' to finish onboarding.",
    targetRoute: '/admin/agents/:agentId',
    requiresAction: false,
    autoAdvance: false,
    completionCheck: null,
  },
  {
    id: 'complete',
    title: "You're All Set!",
    content: "Congratulations! You've successfully set up your Blueprint workspace. Alex is now ready to be your AI teammate. You can start having conversations with Alex, assign tasks, and watch as your AI assistant helps manage your projects. Remember, Alex learns from your knowledge base and gets smarter over time. You can always add more integrations, upload more documents, or add more team members from the sidebar. Welcome to the future of work - let's build something amazing together!",
    targetRoute: '/admin/default',
    requiresAction: false,
    autoAdvance: false, // Final step
    completionCheck: null,
  },
];

